<script setup lang="ts">
import type {TaskType} from "~/types/Task";
import { TaskStateEnum } from '~/types/Task';
const { t } = useI18n();

const props = defineProps({
    task: {
        type: Object as PropType<TaskType>,
        required: true,
    }
});

const createdBy = computed(() => props.task?.createdAt ? formatDate(props.task.createdAt) : null)
const taskBudget = computed(() => {
    if(props.task.budget?.to) return `Up to ${roundMoney(props.task.budget?.to)} AED`
    return t('Negotiable price')
})

const formatDateRange = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};
const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};

const taskDates = computed(() => {
    if(props.task.date?.from && props.task.date?.to) return `${formatDateRange(props.task.date?.from)} - ${formatDateRange(props.task.date?.to)}`
    return t('By arrangement')
})

const statusName = computed(() => {
    switch (props.task?.task_status) {
        case TaskStateEnum.APPLIED:
            return t('Applied');
        case TaskStateEnum.REFUSED:
            return t('Refused');
        case TaskStateEnum.REVIEW:
            return t('On review');
        case TaskStateEnum.ACTIVE:
            return t('Active');
        case TaskStateEnum.POSTED:
            return t('Posted');
        case TaskStateEnum.COMPLETED:
            return t('Completed');
        case TaskStateEnum.CALL_OFF:
            return t('Call off');
        default:
            return t('Pending');
    }
})
</script>

<template>
    <div class="task flex flex-col justify-between bg-stroke h-full">
        <div class="top_row">
            <div class="task__header">
                <div class="task__date">{{ createdBy }}</div>
                <div class="task__status">{{ statusName }}</div>
            </div>
            <div class="task__information mt-7">
                <div class="task__category mb-1">{{ task.specification_name }}</div>
                <div class="task__name font-medium mb-3 break-words">{{ task.name }}</div>
                <div class="task__description break-words line-clamp-4">
                    {{ task.description }}
                </div>
            </div>
        </div>

        <div class="bottom_row flex justify-between mt-7">
            <div class="task__price font-semibold">{{ taskBudget }}</div>

            <div class="task__time font-medium">
                <Icon name="ri:calendar-todo-line" size="24px" class="text-sub mr-2" />
                {{ taskDates }}
            </div>
        </div>
    </div>

</template>
<style scoped lang="scss">
.task {
    padding: 12px 16px;
    border-radius: 12px;
}

.task__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.task__date,
.task__status {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 12px;
    background: #FFFFFF;
    border-radius: 50px;
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: #0A0D14;
}

.task__information {
    flex-grow: 1;
}

.task__category {
    font-family: 'Inter', serif;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: #525866;
    opacity: 0.72;
}

.task__name {
    font-size: 18px;
    line-height: 24px;
}

.task__description {
    font-size: 14px;
    line-clamp: 5;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
}

.task__price,
.task__time {
    font-size: 14px;
    line-height: 20px;
    color: #0A0D14;
    display: flex;
    align-items: center;
}
</style>